import { useMemo, useState } from "react";
import axios from "axios";
import {
  Clock,
  DoorOpen,
  LogIn,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { UscitaTemporaneaDialog, RientroUscitaDialog } from "./UscitaTemporaneaDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDateIt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("it-IT");
}

function sortKey(ut) {
  return `${ut.data_uscita || ""}T${ut.ora_uscita || "00:00"}`;
}

export default function UsciteTemporaneeSection({
  fascicoloId,
  usciteTemporanee = [],
  authHeaders,
  onRefresh,
  canEdit = true,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rientroDialogOpen, setRientroDialogOpen] = useState(false);
  const [editingUscita, setEditingUscita] = useState(null);
  const [rientroUscita, setRientroUscita] = useState(null);
  const [saving, setSaving] = useState(false);

  const sortedUscite = useMemo(
    () =>
      [...(usciteTemporanee || [])].sort((a, b) =>
        sortKey(b).localeCompare(sortKey(a))
      ),
    [usciteTemporanee]
  );

  const inCorsoCount = sortedUscite.filter((u) => !u.data_rientro).length;

  const handleCreateOrUpdate = async (payload) => {
    if (!canEdit || !authHeaders) return;
    setSaving(true);
    try {
      if (editingUscita?.id) {
        await axios.put(
          `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/uscite-temporanee/${editingUscita.id}`,
          payload,
          { headers: authHeaders }
        );
        toast.success("Uscita temporanea aggiornata");
      } else {
        await axios.post(
          `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/uscite-temporanee`,
          payload,
          { headers: authHeaders }
        );
        toast.success("Uscita temporanea registrata");
      }
      setDialogOpen(false);
      setEditingUscita(null);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleRientro = async (payload) => {
    if (!canEdit || !authHeaders || !rientroUscita?.id) return;
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/uscite-temporanee/${rientroUscita.id}/rientro`,
        payload,
        { headers: authHeaders }
      );
      toast.success(
        rientroUscita.data_rientro ? "Rientro aggiornato" : "Rientro registrato"
      );
      setRientroDialogOpen(false);
      setRientroUscita(null);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore registrazione rientro");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ut) => {
    if (!canEdit || !authHeaders) return;
    if (!window.confirm(`Eliminare l'uscita temporanea del ${formatDateIt(ut.data_uscita)}?`)) {
      return;
    }
    try {
      await axios.delete(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/uscite-temporanee/${ut.id}`,
        { headers: authHeaders }
      );
      toast.success("Uscita temporanea eliminata");
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <>
      <Card className="card-base mt-6">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <DoorOpen className="w-5 h-5 text-amber-600" />
            Uscite temporanee
            {inCorsoCount > 0 ? (
              <Badge className="bg-amber-500 text-white">{inCorsoCount} in corso</Badge>
            ) : null}
          </CardTitle>
          {canEdit ? (
            <Button
              size="sm"
              onClick={() => {
                setEditingUscita(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Nuova uscita
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          {sortedUscite.length === 0 ? (
            <p className="text-sm text-slate-500">Nessuna uscita temporanea registrata.</p>
          ) : (
            <ul className="space-y-3">
              {sortedUscite.map((ut) => {
                const rientrato = Boolean(ut.data_rientro);
                return (
                  <li
                    key={ut.id}
                    className="rounded-lg border border-slate-200 p-4 bg-white"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                      <div className="min-w-0 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={rientrato ? "secondary" : "default"} className={!rientrato ? "bg-amber-500 hover:bg-amber-500" : ""}>
                            {rientrato ? "Rientrato" : "In corso"}
                          </Badge>
                          <span className="font-semibold text-slate-900">{ut.motivo}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
                          <p className="flex items-center gap-1.5">
                            <DoorOpen className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                            Uscita: {formatDateIt(ut.data_uscita)} alle {ut.ora_uscita}
                          </p>
                          {rientrato ? (
                            <p className="flex items-center gap-1.5">
                              <LogIn className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                              Rientro: {formatDateIt(ut.data_rientro)} alle {ut.ora_rientro}
                            </p>
                          ) : (
                            <p className="flex items-center gap-1.5 text-amber-700">
                              <Clock className="w-3.5 h-3.5 shrink-0" />
                              Rientro non ancora registrato
                            </p>
                          )}
                        </div>
                        {ut.created_by_nome ? (
                          <p className="text-xs text-slate-400">
                            Registrata da {ut.created_by_nome}
                            {ut.rientro_registrato_by_nome
                              ? ` · Rientro da ${ut.rientro_registrato_by_nome}`
                              : ""}
                          </p>
                        ) : null}
                      </div>
                      {canEdit ? (
                        <div className="flex flex-wrap gap-1.5 shrink-0">
                          {!rientrato ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingUscita(ut);
                                  setDialogOpen(true);
                                }}
                              >
                                <Pencil className="w-3.5 h-3.5 mr-1" />
                                Modifica
                              </Button>
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => {
                                  setRientroUscita(ut);
                                  setRientroDialogOpen(true);
                                }}
                              >
                                <LogIn className="w-3.5 h-3.5 mr-1" />
                                Registra rientro
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setRientroUscita(ut);
                                setRientroDialogOpen(true);
                              }}
                            >
                              <Pencil className="w-3.5 h-3.5 mr-1" />
                              Modifica rientro
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(ut)}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Elimina
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {canEdit ? (
        <>
          <UscitaTemporaneaDialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) setEditingUscita(null);
            }}
            initialData={editingUscita}
            onSubmit={handleCreateOrUpdate}
            saving={saving}
          />
          <RientroUscitaDialog
            open={rientroDialogOpen}
            onOpenChange={(open) => {
              setRientroDialogOpen(open);
              if (!open) setRientroUscita(null);
            }}
            initialData={rientroUscita}
            onSubmit={handleRientro}
            saving={saving}
          />
        </>
      ) : null}
    </>
  );
}
